import React, { useState } from 'react';
import './InfoPage.css';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How is CGPA calculated at VIT?',
      answer: `CGPA (Cumulative Grade Point Average) is calculated by dividing the sum of (grade points × credits) for all courses by the total credits earned. For example, if you scored an S grade (10 points) in a 4-credit course and an A grade (9 points) in a 3-credit course, your CGPA would be: ((10×4) + (9×3)) ÷ (4+3) = 67 ÷ 7 = 9.57.`
    },
    {
      question: 'What is the VIT grading system?',
      answer: `VIT uses a 10-point grading scale:
• S grade = 10 points (90-100 marks) - Outstanding
• A grade = 9 points (80-89 marks) - Excellent
• B grade = 8 points (70-79 marks) - Very Good
• C grade = 7 points (60-69 marks) - Good
• D grade = 6 points (55-59 marks) - Average
• E grade = 5 points (50-54 marks) - Pass
• F grade = 0 points (<50 marks) - Fail
• N grade = 0 points - Not attended`
    },
    {
      question: 'How do I convert CGPA to percentage?',
      answer: `At VIT, the formula to convert CGPA to percentage is: Percentage = CGPA × 10. For instance, a CGPA of 9.2 converts to 92%. This is a standard conversion used across most Indian universities, though some institutions may use slightly different formulas. Always verify with your university's official guidelines.`
    },
    {
      question: 'What is the difference between GPA and CGPA?',
      answer: `GPA (Grade Point Average) refers to your performance in a single semester, calculated using only that semester's courses and credits. CGPA (Cumulative Grade Point Average) represents your overall performance across all completed semesters. Your CGPA is the weighted average of all your semester GPAs based on total credits earned.`
    },
    {
      question: 'Can I improve my CGPA in later semesters?',
      answer: `Yes! Use our Target Calculator to determine what grades you need in remaining credits. For example, if you have a 7.5 CGPA after 80 credits and want to reach 8.0 with 40 credits remaining, you would need approximately an 8.67 average in those courses. Early semesters have the most impact due to higher credit weight.`
    },
    {
      question: 'What happens if I fail a course?',
      answer: `A failed course (F grade = 0 points) significantly impacts your CGPA because it contributes zero grade points while still counting toward total credits. You typically need to retake the course. When you pass the retake, some universities replace the F with the new grade, while others average both attempts—check VIT's specific retake policy.`
    },
    {
      question: 'How many credits do I need to graduate from VIT?',
      answer: `Most VIT B.Tech programs require 160-170 credits over 8 semesters (4 years), though this varies by specialization and campus. Each semester typically has 18-24 credits. Check your specific program curriculum for exact requirements, including mandatory core courses, electives, and project credits.`
    },
    {
      question: 'What is a good CGPA at VIT?',
      answer: `A CGPA above 9.0 is considered excellent and often qualifies you for honors/distinction. 8.0-9.0 is very good and competitive for top placements. 7.0-8.0 is good and meets most graduate school and job requirements. Below 6.0 may require academic counseling. Many top recruiters set cutoffs between 7.0-8.0.`
    },
    {
      question: 'Does attendance affect CGPA?',
      answer: `Attendance doesn't directly affect CGPA calculation, but VIT requires minimum 75% attendance to sit for final exams. If you fall below this threshold without valid exemptions, you may receive an N grade (0 points), which severely impacts CGPA. Additionally, some courses award participation marks that can influence your final grade.`
    },
    {
      question: 'How do I use the Quick CGPA Calculator?',
      answer: `The Quick Calculator is ideal when you know your semester-wise GPAs. Simply add each semester, enter the GPA you achieved and the total credits for that semester, then click Calculate. The tool instantly shows your overall CGPA, percentage, and semester-by-semester breakdown.`
    },
    {
      question: 'When should I use the Detailed Calculator?',
      answer: `Use the Detailed Calculator when you want to input individual course grades and credits. This mode is perfect for tracking each course across multiple semesters, visualizing grade distribution, and seeing exactly which courses contribute most to your CGPA. It's especially useful for planning course selections.`
    },
    {
      question: 'How does the Target Calculator work?',
      answer: `Enter your current CGPA, total credits earned so far, your desired target CGPA, and remaining credits. The calculator shows whether your target is achievable and what average grade you need in remaining courses. For example, moving from 8.5 to 9.0 with 40 credits left typically requires a 9.75+ average.`
    },
    {
      question: 'Is my data saved if I don\'t sign in?',
      answer: `Yes, but only in your browser's local storage. Without an account, your data stays on that device and browser. If you clear browser data, sign out, or switch devices, you'll lose your calculations. Creating a free account syncs your data to the cloud across all your devices.`
    },
    {
      question: 'Is this calculator accurate for all VIT campuses?',
      answer: `This calculator uses the standard VIT 10-point grading scale used across Vellore, Chennai, Bhopal, and Amaravati campuses. However, always cross-verify with your official academic transcript, as internal assessments, bonus points, or policy changes may affect final grades differently across programs.`
    },
    {
      question: 'Can I calculate CGPA for other universities?',
      answer: `This tool is optimized for VIT's 10-point system. For universities using 4.0 scale (common in US/Canada) or different grade mappings, the calculations won't be accurate. If there's demand, we may add multi-university support in future updates—contact us if you'd like to see your university supported.`
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="info-page">
      <div className="page-header">
        <p className="page-eyebrow">Help Center</p>
        <h1>Frequently Asked Questions</h1>
        <p className="page-lede">
          Everything you need to know about CGPA calculation, VIT's grading system,
          and how to make the most of this calculator.
        </p>
      </div>

      <div className="info-card">
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div key={index} className="faq-item">
              <button
                className={`faq-question ${openIndex === index ? 'active' : ''}`}
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
              >
                <span>{faq.question}</span>
                <span className="faq-icon">{openIndex === index ? '−' : '+'}</span>
              </button>
              {openIndex === index && (
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="info-card highlight-card">
        <h3>Still Have Questions?</h3>
        <p>
          Can't find what you're looking for? Our support team typically responds within
          24 hours. Visit our <a href="/contact">Contact page</a> for support options.
        </p>
      </div>
    </div>
  );
};

export default FAQ;
